/**
 * Server-side thread API client (chat v2, AIVA-5929).
 *
 * The server thread store (3-day sliding TTL) is the source of truth for the
 * transcript; localStorage is only a render cache. This module wraps the
 * /api/v2/chat/threads endpoints and maps the wire DTOs into `ChatMessage`s.
 */

import { config } from '../../shared/config/environment';
import { fetchWithRefresh, type RefreshTokenFn } from '../../shared/api/fetchWithRefresh';
import type { Channel } from '../../shared/host';
import {
  buildBookSlotPill,
  tryParseCalendarSources,
  tryParseDraft,
  tryParseEmailSources,
  tryParseListEmailsOutput,
  tryParseNbaSources,
  tryParseSearchResults,
} from '../../../integrations/tools/outlook';
import { guardrailMessage } from '../guardrails';
import type { ThreadSeed } from '../skillMessageSession';
import { buildOutlookPills, resolvePersonaSkillKeys, type OutlookSurface } from '../pills/outlookPills';
import type {
  ApprovalRequest,
  ChatMessage,
  MultipartPlan,
  MultipartStep,
  PendingFrontendTool,
  Pill,
} from '../types';

const THREADS_URL = () => `${config.aiApiBaseUrl}/api/v2/chat/threads`;

/** Thrown when a thread is unknown or expired server-side (3-day TTL). */
export class ThreadNotFoundError extends Error {
  constructor(threadId: string) {
    super(`Thread not found: ${threadId}`);
    this.name = 'ThreadNotFoundError';
  }
}

export interface ThreadSummary {
  thread_id: string;
  title: string;
  host: string;
  context_id: string | null;
  last_skill_key: string | null;
  /** `skill_message` for a thread opened from a skill's Notify message; absent
   *  or null for an ordinary conversation. */
  origin?: string | null;
  message_count: number;
  created_at: string;
  updated_at: string;
}

/** A thread the pane reaches only by opening its skill message, never by adoption. */
export function isSkillMessageThread(t: Pick<ThreadSummary, 'origin'>): boolean {
  return t.origin === 'skill_message';
}

export interface ThreadMessageDTO {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  reasoning?: string;
  pills?: Array<Record<string, unknown>>;
  skill_key?: string | null;
  status?: string;
  pending_interrupt?: { kind: string; payload: Record<string, unknown> } | null;
  /** An interrupt the user already answered, plus their decision (AIVA-6899). */
  resolved_interrupt?: { kind: string; payload: Record<string, unknown>; decision?: string } | null;
  guardrail_categories?: string[];
  /** Which screened inputs flagged ("message" / "email" / "document" /
   *  "attachment"). Absent on messages stored before attribution existed
   *  (AIVA-6560) — those read as the user's own message. */
  guardrail_sources?: string[];
  /**
   * AIVA-7622 — raw TOOL_END output captured this turn, keyed by tool name,
   * for any tool on the backend's rehydration whitelist (`TurnSink.tool_results`
   * / `_REHYDRATION_CAPTURED_TOOLS` in stream.py): draft/attachment tools and
   * email/calendar search tools. `mapThreadMessage` replays these through the
   * SAME parsers the live turn uses (`integrations/tools/outlook/parsers.ts`)
   * via `TOOL_RESULT_REBUILDERS` below, to rebuild the Outlook action row and
   * result cards on rehydration.
   */
  tool_results?: Record<string, string> | null;
  /** Sequential-multipart plan proposed by this message ({ steps: [...] }).
   *  Only the steps are persisted server-side; how far the user got is
   *  recovered from the transcript — see `resolveMultipartCursors`. */
  multipart_plan?: { steps?: Array<Record<string, unknown>> } | null;
  created_at: string;
}

function buildHeaders(apiToken: string | null, contentType?: string): Record<string, string> {
  const headers: Record<string, string> = {};
  if (contentType) headers['Content-Type'] = contentType;
  if (apiToken) headers['Authorization'] = `Bearer ${apiToken}`;
  return headers;
}

export async function listThreads(
  apiToken: string | null,
  host: string,
  contextId: string | null,
  refresh?: RefreshTokenFn
): Promise<ThreadSummary[]> {
  const response = await fetchWithRefresh(
    THREADS_URL(),
    { headers: buildHeaders(apiToken), cache: 'no-store' },
    refresh
  );
  if (!response.ok) {
    throw new Error(`Thread list fetch failed: ${response.status}`);
  }
  const payload = (await response.json()) as { threads: ThreadSummary[] };
  // Filter client-side by host + context so per-context isolation (Word doc
  // URL keying, Outlook host bucket) matches the localStorage rules.
  return payload.threads.filter((t) => t.host === host && (t.context_id ?? null) === (contextId ?? null));
}

export async function getThreadMessages(
  apiToken: string | null,
  threadId: string,
  refresh?: RefreshTokenFn
): Promise<ThreadMessageDTO[]> {
  const response = await fetchWithRefresh(
    `${THREADS_URL()}/${encodeURIComponent(threadId)}/messages`,
    { headers: buildHeaders(apiToken), cache: 'no-store' },
    refresh
  );
  if (response.status === 404) {
    throw new ThreadNotFoundError(threadId);
  }
  if (!response.ok) {
    throw new Error(`Thread messages fetch failed: ${response.status}`);
  }
  const payload = (await response.json()) as { messages: ThreadMessageDTO[] };
  return payload.messages;
}

/**
 * Create a server thread. With a `seed`, the thread opens with that assistant
 * message and its quick-reply pills (a skill message's chat session).
 */
export async function createThread(
  apiToken: string | null,
  host: string,
  contextId: string | null,
  refresh?: RefreshTokenFn,
  seed?: ThreadSeed
): Promise<string> {
  const response = await fetchWithRefresh(
    THREADS_URL(),
    {
      method: 'POST',
      headers: buildHeaders(apiToken, 'application/json'),
      body: JSON.stringify({ host, context_id: contextId, seed }),
    },
    refresh
  );
  if (!response.ok) {
    throw new Error(`Thread create failed: ${response.status}`);
  }
  const payload = (await response.json()) as { thread_id: string };
  return payload.thread_id;
}

/**
 * Map a persisted pill payload back to the renderable `Pill` union.
 *
 * Note what `message.pills` actually holds: backend-emitted `display_pill`
 * descriptors, which are `{handler: "ui.pill.*", args}` TOOL INVOCATIONS, not
 * rendered pills (see shared/tools/followup_pills.py). The label and the real
 * office handler are both derived by the add-in's own `ui.pill.*` handler when it
 * runs — `ui.pill.office.calendar_open` resolves to a pill whose handler is
 * `calendar.open_teams`, for instance. So most such descriptors are NOT
 * renderable and are rejected here (AIVA-6899): mapping one would produce a
 * button that, on click, re-emits a pill instead of performing the action, and
 * would survive `dedupePills` alongside the real cached pill as a duplicate.
 * Rehydrated pills otherwise come from the localStorage render cache instead
 * — see threadReconcile.ts.
 *
 * `ui.pill.chat.book_slot` (AIVA-7622) is the one exception: unlike the other
 * `ui.pill.*` handlers, its live resolution (`buildBookSlotPill`, handlers.ts)
 * is a pure formatter over args the descriptor already carries in full
 * (start/end ISO, subject, attendees, availability note) — no live calendar
 * read, no re-emission loop — so calling it here on rehydration is exactly as
 * safe as the live turn calling it at TOOL_END. Showing the actual slots the
 * user was offered (accepting that one might since have been booked, same
 * staleness every other rehydrated pill already accepts — design doc A3/B7)
 * beats showing nothing.
 *
 * Descriptors that ARE renderable still map: a `chat_intent` (`{label, message}`)
 * and an office action naming a real handler (`{label, handler: "compose.open"}`).
 */
function mapPill(raw: Record<string, unknown>): Pill | null {
  if (raw.handler === 'ui.pill.chat.book_slot') {
    return buildBookSlotPill((raw.args as Record<string, unknown>) ?? {});
  }
  const label = typeof raw.label === 'string' ? raw.label : null;
  if (!label) return null;
  if (typeof raw.handler === 'string' && raw.handler.startsWith('ui.pill.')) return null;
  if (typeof raw.handler === 'string') {
    return {
      type: 'office_action',
      label,
      handler: raw.handler,
      args: (raw.args as Record<string, unknown>) ?? {},
    };
  }
  if (typeof raw.message === 'string') {
    // Preserve `hidden` (machine-readable chat_intent messages) across
    // rehydration — mapAggregatePill in turnEvents keeps it for live turns.
    const pill: Pill = { type: 'chat_intent', label, message: raw.message };
    if (raw.hidden === true) pill.hidden = true;
    if (raw.booksSlot === true) pill.booksSlot = true;
    if (typeof raw.bubble_text === 'string' && raw.bubble_text) pill.bubbleText = raw.bubble_text;
    return pill;
  }
  return null;
}

/** Map a persisted approval interrupt payload into the card-renderable shape. */
function mapApprovalInterrupt(payload: Record<string, unknown>): ApprovalRequest {
  return {
    interrupt_id: typeof payload.interrupt_id === 'string' ? payload.interrupt_id : '',
    tool: typeof payload.tool === 'string' ? payload.tool : '',
    args: (payload.args as Record<string, unknown>) ?? {},
    description: typeof payload.description === 'string' ? payload.description : undefined,
  };
}

/**
 * The approval card to render for a persisted message, or null when it carries
 * none (AIVA-6899).
 *
 * Approval cards are entirely backend-owned — the payload comes from the
 * ``approval_required`` event and the decision from the resume request — so the
 * store now keeps them for their whole lifecycle: ``pending_interrupt`` while
 * the decision is outstanding, ``resolved_interrupt`` (carrying ``decision``)
 * once answered. Both rehydrate, on any device.
 */
function readApproval(dto: ThreadMessageDTO): ApprovalRequest | null {
  const resolved = dto.resolved_interrupt;
  if (resolved?.kind === 'approval') {
    const answered = mapApprovalInterrupt(resolved.payload);
    if (resolved.decision === 'approve' || resolved.decision === 'reject')
      answered.decision = resolved.decision;
    return answered;
  }
  const pending = dto.pending_interrupt;
  if (pending?.kind !== 'approval') return null;
  const card = mapApprovalInterrupt(pending.payload);
  // Parked but no longer interrupted server-side: there is nothing left to
  // resume, so the buttons must not come back looking live.
  if (dto.status !== 'interrupted') card.stale = true;
  return card;
}

/** Map a parked frontend-tool interrupt; null when the payload is unusable. */
function mapFrontendToolInterrupt(payload: Record<string, unknown>): PendingFrontendTool | null {
  const toolCallId = typeof payload.tool_call_id === 'string' ? payload.tool_call_id : '';
  const handler = typeof payload.handler === 'string' ? payload.handler : '';
  if (!toolCallId || !handler) return null;
  return {
    tool_call_id: toolCallId,
    handler,
    args: (payload.args as Record<string, unknown>) ?? {},
    timeout_ms: typeof payload.timeout_ms === 'number' ? payload.timeout_ms : 30000,
  };
}

/**
 * Live, per-viewer facts `mapThreadMessage` needs to rebuild the Outlook
 * action row on rehydration (AIVA-7622) — everything ELSE it needs
 * (skill_key, the streamed answer, the captured tool results) is already on
 * the DTO. Omit entirely for non-Outlook rehydration (Word/PowerPoint) or
 * callers that don't need the row (most existing tests) — the row is simply
 * not built.
 */
export interface OutlookPillRehydrationContext {
  readonly host: Channel | null;
  readonly surface: OutlookSurface | null;
}

/**
 * AIVA-7622 — the add-in's mirror of the backend's `_REHYDRATION_CAPTURED_TOOLS`
 * (stream.py). Each entry re-parses a captured tool's raw TOOL_END output with
 * the SAME parser the live turn already uses to build the SAME UI
 * (`integrations/tools/outlook/parsers.ts`), and returns the `ChatMessage`
 * patch it produces live. Giving a new skill working rehydration is then: list
 * its tool in the backend whitelist, and add one row here that calls the
 * parser it already has — nothing else changes on either side.
 *
 * Draft-producing tools (submit_email_draft / create_reply_draft /
 * create_compose_draft) are deliberately NOT in this table — their output
 * feeds `buildOutlookPills` as an argument rather than patching the message
 * directly, and the attachment tools also synthesize an "Open draft" pill the
 * way the live turn does — see the fallback block in `mapThreadMessage` below.
 */
const TOOL_RESULT_REBUILDERS: Readonly<Record<string, (output: string) => Partial<ChatMessage> | null>> = {
  submit_email_search_results: (output) => {
    const parsed = tryParseSearchResults(output);
    if (!parsed) return null;
    return {
      searchResults: parsed.results,
      searchQuerySummary: parsed.querySummary,
      ...(parsed.searchWindow && { searchWindow: parsed.searchWindow }),
    };
  },
  fetch_emails: (output) => {
    const parsed = tryParseEmailSources(output);
    return parsed ? { searchResults: parsed.results, searchQuerySummary: parsed.querySummary } : null;
  },
  list_emails_obo: (output) => {
    const results = tryParseListEmailsOutput(output);
    return results ? { searchResults: results, searchQuerySummary: 'Related emails' } : null;
  },
  get_nbas: (output) => {
    const parsed = tryParseNbaSources(output);
    return parsed ? { searchResults: parsed.results, searchQuerySummary: parsed.querySummary } : null;
  },
  fetch_calendar_events: (output) => {
    const sources = tryParseCalendarSources(output);
    return sources ? { calendarSources: sources } : null;
  },
  manage_calendar: (output) => {
    const sources = tryParseCalendarSources(output);
    return sources ? { calendarSources: sources } : null;
  },
  produce_agenda: (output) => {
    const sources = tryParseCalendarSources(output);
    return sources ? { calendarSources: sources } : null;
  },
};

/**
 * The draft_id create_reply_draft/create_compose_draft return on success —
 * mirrors `handleCreateDraftToolEnd`'s own inline parse in turnEvents.ts,
 * which is why this stays a narrow one-field parse rather than a full
 * `EmailDraft` shape: that's genuinely all the tool returns.
 */
function tryParseDraftId(output: string): string | null {
  try {
    const parsed = JSON.parse(output) as { draft_id?: unknown };
    return typeof parsed.draft_id === 'string' && parsed.draft_id ? parsed.draft_id : null;
  } catch {
    return null;
  }
}

/**
 * Replay every captured tool result through the SAME parser the live turn
 * used, rebuilding whatever client-only UI it produced (search and calendar
 * result cards). Extracted from `mapThreadMessage` to keep its own cognitive
 * complexity under the Sonar threshold (S3776) — mirrors why `nothingToActOn`
 * is split out of `buildOutlookPills` elsewhere in this codebase.
 *
 * Iteration follows `Object.entries` insertion order, which mirrors the order
 * TOOL_END events actually streamed in — so a turn that called two
 * card-producing tools ends up with the LAST one's cards, the same
 * last-call-wins rule the backend's capture already applies.
 */
/**
 * Re-apply the guardrail notice to a rehydrated message (AIVA-6560).
 *
 * The stored `content` of a flagged turn is the PARTIAL response that streamed
 * before the verdict landed — the backend persists `sink.content` and never
 * clears it, and the live turn only replaces it in the pane. So a relaunch
 * used to re-render exactly the text the screen had suppressed. Rebuilding the
 * notice here makes rehydration agree with the live turn, and `sources` makes
 * it agree on WHICH input flagged: without them a reloaded thread silently
 * downgrades "the open email was flagged" to "your message was flagged".
 *
 * Copy lives in `guardrailMessage` rather than on the wire so product can tune
 * it without a backend deploy — the same reason the live path composes it.
 */
function applyGuardrailNotice(message: ChatMessage, dto: ThreadMessageDTO): void {
  if (!dto.guardrail_categories?.length) return;
  message.guardrailCategories = dto.guardrail_categories;
  const sources = dto.guardrail_sources ?? [];
  if (sources.length) message.guardrailSources = sources;
  message.content = guardrailMessage(dto.guardrail_categories, sources);
}

function applyCapturedToolResults(
  message: ChatMessage,
  toolResults: Record<string, string> | null | undefined
): void {
  if (!toolResults) return;
  for (const [tool, output] of Object.entries(toolResults)) {
    const patch = TOOL_RESULT_REBUILDERS[tool]?.(output);
    if (patch) Object.assign(message, patch);
  }
}

/**
 * Rebuild the Outlook action row buildOutlookPills produces live at `done` —
 * never persisted itself, only its raw ingredients (skill_key, content,
 * captured tool results) are. Extracted from `mapThreadMessage` for the same
 * complexity reason as {@link applyCapturedToolResults}; callers must already
 * have checked `!dto.pills?.length` (see that function for why).
 */
function rebuildOutlookActionRow(
  dto: ThreadMessageDTO,
  skillKey: string,
  outlook: OutlookPillRehydrationContext
): Pill[] {
  // Whichever draft-producing tool succeeded is a key in `tool_results` (only
  // successes are captured at all — see TurnSink.tool_results), so this is no
  // longer a hardcoded guess at which tool the skill needed. This is what
  // fixes the attachment skills (send_attachment_draft/_compose): their
  // create_reply_draft/create_compose_draft never carry a body to capture,
  // but DO need to register as succeeded for Book meeting/Copy to reappear —
  // previously nothing recorded that and the whole row vanished.
  const succeededTools = new Set(Object.keys(dto.tool_results ?? {}));
  const draftOutput = dto.tool_results?.submit_email_draft;
  const draft = draftOutput ? tryParseDraft(draftOutput) : null;
  const pills = buildOutlookPills({
    skillKey,
    surface: outlook.surface,
    answerText: dto.content,
    draft,
    personaSkillKeys: resolvePersonaSkillKeys(outlook.host),
    succeededTools,
    // failedTools and listEmailsResultCount are deliberately omitted (see
    // AIVA-7622 notes): without them an apology or an empty-listing turn may
    // show buttons it shouldn't on rehydration, which is the opposite
    // direction from the bug this closes and lower severity.
    // promptText intentionally omitted — falls back to the skill's own bar,
    // exactly as buildOutlookPills already documents for this caller.
  });
  // The attachment tools' "Reply"/"Open" pill is emitted deterministically,
  // client-side, straight off create_reply_draft/create_compose_draft's own
  // draft_id — never through the OUTLOOK_BARS table buildOutlookPills reads
  // (handleCreateDraftToolEnd in turnEvents.ts is the live equivalent) — so
  // it is added here rather than folded into buildOutlookPills, which has no
  // notion of a draft id. `unshift`, not `push`: live, this pill is emitted
  // at TOOL_END, ahead of the bar buildOutlookPills assembles at `done`, so
  // it renders first (Reply · Book meeting · Copy) — rehydration matches that
  // order rather than tacking it on the end.
  const replyDraftOutput = dto.tool_results?.create_reply_draft;
  const composeDraftOutput = dto.tool_results?.create_compose_draft;
  const attachmentDraftOutput = replyDraftOutput ?? composeDraftOutput;
  const attachmentDraftId = attachmentDraftOutput ? tryParseDraftId(attachmentDraftOutput) : null;
  if (attachmentDraftId) {
    pills.unshift({
      type: 'office_action',
      label: replyDraftOutput ? 'Reply' : 'Open',
      handler: 'attachment.open_draft',
      args: { draft_id: attachmentDraftId },
    });
  }
  return pills;
}

/** Map one persisted plan step, or null when the payload is not a step. */
function mapMultipartStep(raw: Record<string, unknown>): MultipartStep | null {
  const skillKey = typeof raw.skillKey === 'string' ? raw.skillKey : null;
  const label = typeof raw.label === 'string' ? raw.label : null;
  const subTask = typeof raw.subTask === 'string' ? raw.subTask : null;
  if (!skillKey || !label || !subTask) return null;
  return { skillKey, label, subTask };
}

/**
 * Rebuild a thread's multipart cards from the transcript itself.
 *
 * Only the STEPS are persisted server-side; how far the user got is not. The
 * queue advances on every click, and persisting each advance would mean a write
 * per click — which it does not need, because each confirmed step runs as its
 * own turn pinned to that step's skill, so the step's `skillKey` appears as the
 * `skill_key` of a LATER assistant message. Counting the leading steps that
 * appear after the proposal reconstructs the position exactly, from data the
 * thread store already holds.
 *
 * Leading run only — the count stops at the first step with no matching later
 * message. A skipped step therefore reads as "still to offer" after a reopen,
 * and so does a whole cancelled queue (both are client-only state and are not
 * persisted). Both err in the same, safe direction: re-asking about work the
 * user declined costs one click and nothing runs without one, whereas skipping
 * past it would silently drop work they did want.
 *
 * The live cards are then laid out the way they were before the reopen:
 *
 * * the proposal message keeps its card, settled, as the record of what was
 *   offered — it must not still be asking about a step that has already run;
 * * the outstanding question opens as a COMPACT card on the newest message, so
 *   it sits below the last answer exactly as it would have live.
 *
 * The intermediate cards are not recovered: they were anchored to messages by
 * the queue as it ran, and that anchoring is not persisted. The transcript still
 * carries every step's own answer, which is the part that matters.
 */
export function resolveMultipartCursors(messages: ChatMessage[]): ChatMessage[] {
  const out = [...messages];
  for (let index = 0; index < out.length; index++) {
    const plan = out[index]?.multipartPlan;
    if (!plan) continue;

    const laterSkillKeys = new Set(
      out.slice(index + 1).flatMap((m) => (m.role === 'assistant' && m.skillKey ? [m.skillKey] : []))
    );
    let cursor = 0;
    while (cursor < plan.steps.length && laterSkillKeys.has(plan.steps[cursor]!.skillKey)) {
      cursor += 1;
    }
    // Nothing ran: the proposal card is still the open question, exactly as it
    // was streamed.
    if (cursor === 0) continue;

    out[index] = { ...out[index]!, multipartPlan: { ...plan, cursor: 0, outcome: 'ran' } };
    if (cursor >= plan.steps.length) continue;

    const newest = out.length - 1;
    if (newest <= index) continue;
    out[newest] = {
      ...out[newest]!,
      multipartPlan: { steps: plan.steps, cursor, compact: true },
    };
  }
  return out;
}

/**
 * Should the Outlook action row be rebuilt from scratch for this message?
 *
 * Extracted as a named predicate to keep `mapThreadMessage` under the cognitive
 * complexity limit (sonar typescript:S3776) — four conditions on one line was
 * also the least readable part of that function.
 *
 * True ONLY when `dto.pills` never existed at all, NOT merely when it failed to
 * map into something renderable. Those are different facts: a draft-producing
 * skill (email_draft) has no pills field, so this IS the right call to make. A
 * skill whose pills DID map — calendar_assistant's Open-in-calendar pill, or its
 * slot-picker cards — already has a real row above and must not reach here.
 * Falling back to a generic bar for a turn that HAD pills, just none that
 * mapped, would REPLACE a correctly-empty row with a plausible but wrong one,
 * which is worse than showing nothing.
 */
function needsOutlookRowRebuild(
  dto: ThreadMessageDTO,
  outlook: OutlookPillRehydrationContext | undefined
): boolean {
  return (
    !dto.pills?.length && outlook?.host === 'outlook' && dto.role === 'assistant' && Boolean(dto.skill_key)
  );
}

/**
 * The multipart plan a persisted message carries, or `undefined`.
 *
 * Extracted from `mapThreadMessage` to keep it under the cognitive complexity
 * limit (sonar typescript:S3776). The steps come back from the store; the
 * cursor is recovered by `resolveMultipartCursors` once the whole transcript is
 * mapped, since it depends on the messages that FOLLOW this one.
 *
 * Fewer than two steps is not a plan — the backend refuses to emit one, so this
 * only guards a partial payload.
 */
function readMultipartPlan(dto: ThreadMessageDTO): MultipartPlan | undefined {
  const steps = (dto.multipart_plan?.steps ?? [])
    .map(mapMultipartStep)
    .filter((s): s is MultipartStep => s !== null);
  return steps.length >= 2 ? { steps, cursor: 0 } : undefined;
}

/**
 * Map a server thread message into the renderable `ChatMessage` shape.
 * Server ids get an `srv-` prefix so they never collide with the client's
 * `msg-{n}` counter ids.
 */
export function mapThreadMessage(
  dto: ThreadMessageDTO,
  outlook?: OutlookPillRehydrationContext
): ChatMessage {
  const message: ChatMessage = {
    id: `srv-${dto.id}`,
    role: dto.role,
    content: dto.content,
    timestamp: Date.parse(dto.created_at) || undefined,
  };
  if (dto.reasoning) {
    message.reasoning = dto.reasoning;
  }
  if (dto.skill_key) {
    message.skillKey = dto.skill_key;
  }
  if (dto.pills?.length) {
    const pills = dto.pills.map(mapPill).filter((p): p is Pill => p !== null);
    if (pills.length) message.actionPills = pills;
  }
  // AIVA-7622: rebuild whatever client-only UI a captured tool result produced
  // live (search/calendar result cards) — see applyCapturedToolResults.
  applyCapturedToolResults(message, dto.tool_results);
  // AIVA-7622 — see needsOutlookRowRebuild for why this is gated so tightly.
  if (needsOutlookRowRebuild(dto, outlook)) {
    const pills = rebuildOutlookActionRow(dto, dto.skill_key!, outlook!);
    if (pills.length) message.actionPills = pills;
  }
  applyGuardrailNotice(message, dto);
  const multipartPlan = readMultipartPlan(dto);
  if (multipartPlan) message.multipartPlan = multipartPlan;
  // Re-surface the approval card — undecided OR already answered.
  const approval = readApproval(dto);
  if (approval) {
    message.approval = approval;
  }
  // Unlike an approval card, a frontend-tool interrupt is DISPATCHED on
  // rehydration, so it stays gated on `status`: replaying a settled one would
  // re-run the tool.
  const interrupt = dto.status === 'interrupted' ? dto.pending_interrupt : null;
  // Re-surface a parked frontend-tool interrupt so useChat can dispatch it
  // and resume the paused turn — dropping it here stranded the thread in
  // "interrupted" with no way to continue after the pane was reopened.
  if (interrupt?.kind === 'frontend_tool') {
    const pending = mapFrontendToolInterrupt(interrupt.payload);
    if (pending) message.pendingFrontendTool = pending;
  }
  return message;
}
